import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BrandLogo } from '@/components/BrandLogo';

const ERROR_MESSAGES: Record<string, string> = {
  state_mismatch: 'Phiên đăng nhập không hợp lệ. Vui lòng thử lại.',
  oauth_failed: 'Đăng nhập KSystem thất bại. Vui lòng thử lại.',
  not_allowed: 'Email của bạn chưa được cấp quyền truy cập. Vui lòng liên hệ admin.',
  disabled: 'Tài khoản đã bị vô hiệu hoá.',
};

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isDev = import.meta.env.MODE !== 'production';

  useEffect(() => {
    const err = searchParams.get('error');
    if (err) {
      setError(ERROR_MESSAGES[err] ?? 'Đăng nhập thất bại.');
    }
  }, [searchParams]);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        navigate('/transactions');
      } else {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error === 'invalid_password' ? 'Sai mật khẩu.' : 'Đăng nhập thất bại.');
      }
    } catch (err) {
      setError(`Lỗi mạng: ${String(err)}`);
    } finally {
      setLoading(false);
    }
  }

  function loginWithKSystem() {
    window.location.href = '/api/auth/oauth/start';
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <Card className="w-full max-w-md p-10">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <BrandLogo size={56} />
          <div className="space-y-1">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70">
              Internal · Kaopiz
            </p>
            <h1
              className="font-heading text-3xl font-semibold text-foreground"
              style={{ letterSpacing: '0.02em' }}
            >
              CARD SYNC
            </h1>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button
          type="button"
          variant="default"
          size="lg"
          className="mb-4 w-full"
          onClick={loginWithKSystem}
        >
          Đăng nhập bằng KSystem
        </Button>

        {isDev && (
          <>
            <div className="my-4 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              <span>Dev fallback</span>
              <span className="h-px flex-1 bg-border" />
            </div>
            <form onSubmit={submitPassword}>
              <div className="mb-3 space-y-1.5">
                <Label htmlFor="password">Password (dev)</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password (dev)"
                />
              </div>
              <Button
                type="submit"
                variant="secondary"
                size="default"
                disabled={loading || !password}
                className="w-full"
              >
                {loading ? 'Đang đăng nhập…' : 'Đăng nhập bằng password'}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
